import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { businessConsultantService } from '../../services/businessConsultantService';
import { businessService } from '../../services/businessService';
import { BusinessConsultantChat } from '../../types/businessConsultant';
import type { Business } from '../../types/business';
import BusinessConsultantHubHeader from './components/BusinessConsultantHubHeader';
import BusinessConsultantCommandPalette from './components/BusinessConsultantCommandPalette';
import ChatTab from './tabs/ChatTab';
import KnowledgeTab from './tabs/KnowledgeTab';
import HistoryTab from './tabs/HistoryTab';
import SettingsTab from './tabs/SettingsTab';

type TabId = 'chat' | 'knowledge' | 'history' | 'settings';

interface TabComponentProps {
  chats?: BusinessConsultantChat[];
  setChats?: React.Dispatch<React.SetStateAction<BusinessConsultantChat[]>>;
  businesses?: Business[];
  selectedBusinessId?: string | null;
  onNewChat?: () => void;
  onSelectChat?: (chat: BusinessConsultantChat) => void;
  selectedChat?: BusinessConsultantChat | null;
}

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.ComponentType<TabComponentProps>;
}

const TABS: Tab[] = [
  { id: 'chat', label: 'Chat', icon: '💬', component: ChatTab },
  { id: 'knowledge', label: 'Knowledge', icon: '📚', component: KnowledgeTab },
  { id: 'history', label: 'History', icon: '🕐', component: HistoryTab },
  { id: 'settings', label: 'Settings', icon: '⚙️', component: SettingsTab }
];

const BusinessConsultantHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedBusinessStorageKey = 'selectedBusinessId';
  
  const [activeTab, setActiveTab] = useState<TabId>('chat');
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [chats, setChats] = useState<BusinessConsultantChat[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedChat, setSelectedChat] = useState<BusinessConsultantChat | null>(null);

  const setSelectedBusinessIdAndPersist = (businessId: string | null) => {
    setSelectedBusinessId(businessId);
    try {
      if (businessId) {
        localStorage.setItem(selectedBusinessStorageKey, businessId);
      } else {
        localStorage.removeItem(selectedBusinessStorageKey);
      }
    } catch {
      // ignore
    }
  };

  // Check consent and load data
  useEffect(() => {
    const initialize = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoading(true);
        
        // Check consent
        const consentStatus = await businessConsultantService.getConsentStatus();
        setHasConsented(consentStatus);
        
        if (!consentStatus) {
          setShowTermsModal(true);
        } else {
          // Load chats and businesses
          const [userChats, businessList] = await Promise.all([
            businessConsultantService.getChats(),
            businessService.list()
          ]);
          setChats(userChats);
          setBusinesses(businessList);

          // Resolve business selection: saved -> default -> first -> null
          let savedBusinessId: string | null = null;
          try {
            savedBusinessId = localStorage.getItem(selectedBusinessStorageKey);
          } catch {
            savedBusinessId = null;
          }
          const defaultBusiness = businessList.find((b) => b.is_default);
          const resolvedBusinessId =
            (savedBusinessId && businessList.some((b) => b.id === savedBusinessId) ? savedBusinessId : null) ||
            defaultBusiness?.id ||
            (businessList[0]?.id || null);
          setSelectedBusinessIdAndPersist(resolvedBusinessId);
        }
      } catch (err: any) {
        console.error('Failed to initialize:', err);
        toast.error(err?.message || 'Failed to load consultant');
      } finally {
        setIsLoading(false);
      }
    };

    void initialize();
  }, [user?.id]);

  // Set document title
  useEffect(() => {
    document.title = 'AI Business Consultant | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Parse active tab from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.find(t => t.id === hash)) {
      setActiveTab(hash as TabId);
    }
  }, [location.hash]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Escape to close command palette
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  const handleAcceptTerms = async () => {
    try {
      await businessConsultantService.recordConsent(true);
      setHasConsented(true);
      setShowTermsModal(false);
      toast.success('You can now use the AI Business Consultant!');
      
      // Load chats and businesses after consent
      const [userChats, businessList] = await Promise.all([
        businessConsultantService.getChats(),
        businessService.list()
      ]);
      setChats(userChats);
      setBusinesses(businessList);
      
      // Set default business
      let savedBusinessId: string | null = null;
      try {
        savedBusinessId = localStorage.getItem(selectedBusinessStorageKey);
      } catch {
        savedBusinessId = null;
      }
      const defaultBusiness = businessList.find((b) => b.is_default);
      const resolvedBusinessId =
        (savedBusinessId && businessList.some((b) => b.id === savedBusinessId) ? savedBusinessId : null) ||
        defaultBusiness?.id ||
        (businessList[0]?.id || null);
      setSelectedBusinessIdAndPersist(resolvedBusinessId);
    } catch (err) {
      console.error('Error recording consent:', err);
      toast.error('Failed to record consent. Please try again.');
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (showTermsModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Terms of Service</h2>
          <div className="prose prose-sm max-h-96 overflow-y-auto mb-6">
            <p className="text-gray-600">
              By using the AI Business Consultant, you agree to provide accurate information
              and understand that the AI provides guidance based on the information you share.
              The consultant's advice should be considered as suggestions and not professional
              legal, financial, or medical advice.
            </p>
            <p className="text-gray-600 mt-4">
              We collect and process conversation data to improve the service and provide
              personalized assistance. Your data is encrypted and handled according to our
              privacy policy.
            </p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleAcceptTerms}
              className="flex-1 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              Accept & Continue
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading AI Business Consultant...</div>
          </div>
        </div>
      </div>
    );
  }

  const ActiveTabComponent = TABS.find(t => t.id === activeTab)?.component || ChatTab;
  
  const handleSelectChat = (chat: BusinessConsultantChat) => {
    setSelectedChat(chat);
    setActiveTab('chat');
    navigate('#chat');
  };
  
  const tabProps: TabComponentProps = {
    chats,
    setChats,
    businesses,
    selectedBusinessId,
    selectedChat,
    onNewChat: () => {
      setSelectedChat(null); // Clear selected chat for new conversation
      setActiveTab('chat');
      navigate('#chat');
    },
    onSelectChat: handleSelectChat
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <BusinessConsultantHubHeader 
          chatsCount={chats.length}
          businesses={businesses}
          selectedBusinessId={selectedBusinessId}
          onBusinessChange={setSelectedBusinessIdAndPersist}
          onNewChat={() => {
            setActiveTab('chat');
            navigate('#chat');
          }}
        />

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6" aria-label="Tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                    ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <ActiveTabComponent {...tabProps} />
          </div>
        </div>

        {/* Command Palette Hint */}
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowCommandPalette(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm transition hover:bg-gray-50"
          >
            <span>Press</span>
            <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold">⌘K</kbd>
            <span>for quick actions</span>
          </button>
        </div>
      </div>

      {/* Command Palette */}
      <BusinessConsultantCommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={(tabId: string) => {
          handleTabChange(tabId as TabId);
          setShowCommandPalette(false);
        }}
        chats={chats}
      />
    </div>
  );
};

export default BusinessConsultantHub;
